// 職責：由門店登入回傳的 functions[] 組合出「這個帳號有什麼權限」——純函式，session store 與員工模組共用

/*
 * 這一版**沒有**「權限表空 ⇒ 全放行」：目錄 + SyncService 閉包令每個受限帳號都一定有列，
 * 空集就確實是什麼都沒有（fail-closed）。唯一例外是 `custom_flag='99'`（後端本身回傳全部功能）。
 *
 * 未知碼不會靜默遺失 —— 收集在 `unknownCodes`，員工模組的「權限對照」分頁會列出來，
 * 令 control-plane 嵌入的那份目錄與本 build 的版本錯配立即現形。
 */

import { FunctionType, SUPER_USER_CUSTOM_FLAG } from '@/domain/enums'
import type { Employee, FunctionNode, Role } from '@/domain/types'

import { STORE_CODE_PREFIX, expand, parseCode, type PermissionKey } from './catalog'

export interface PermissionState {
  /** 已展開閉包的葉 key；陣列而非 Set，才可以原樣序列化寫入 localStorage */
  granted: PermissionKey[]
  /** `shopmgr-*` 但目錄不認得的店碼 */
  unknownCodes: string[]
}

export const EMPTY_PERMISSION_STATE: PermissionState = Object.freeze({
  granted: [],
  unknownCodes: [],
}) as PermissionState

/**
 * type=5 的 `shopmgr-*` 列 + type=2 的 POS 碼 → key → 閉包。
 * 舊 type=5 verb 列（`updata` / `add`…）與 type=1 選單列一律忽略：它們是舊後台的東西。
 */
export function buildPermissionState(functions: readonly FunctionNode[]): PermissionState {
  const keys: string[] = []
  const unknownCodes: string[] = []
  for (const fn of functions) {
    if (!fn.enabled) continue
    const code = fn.code.trim()
    if (fn.type === FunctionType.Button) {
      if (!code.startsWith(STORE_CODE_PREFIX)) continue
      const key = parseCode(code, fn.type)
      if (key === null) unknownCodes.push(code)
      else keys.push(key)
    } else if (fn.type === FunctionType.Feature) {
      const key = parseCode(code, fn.type)
      if (key !== null) keys.push(key)
    }
  }
  const { granted } = expand(keys)
  return { granted: [...granted], unknownCodes: [...new Set(unknownCodes)] }
}

export function toPermissionSet(state: PermissionState): ReadonlySet<PermissionKey> {
  return new Set(state.granted)
}

/** 超級用戶直接通過；否則集合中必須確實有這個 key。沒有「空集放行」。 */
export function hasPermission(
  set: ReadonlySet<string>,
  key: PermissionKey,
  isSuperUser: boolean,
): boolean {
  return isSuperUser || set.has(key)
}

/**
 * 員工模組「ops 帳號存取」分頁用：由角色 → 功能列反推有效權限（不需要該帳號登入）。
 * 與 `buildPermissionState` 走同一條路徑，所以兩邊看到的結果一定相同。
 */
export function effectivePermissions(
  employee: Employee,
  roleById: ReadonlyMap<number, Role>,
  functionById: ReadonlyMap<number, FunctionNode>,
): PermissionState & { isSuperUser: boolean } {
  const isSuperUser = employee.customFlag.trim() === SUPER_USER_CUSTOM_FLAG
  const nodes: FunctionNode[] = []
  const seen = new Set<number>()
  for (const roleId of employee.roleIds) {
    const role = roleById.get(roleId)
    if (role === undefined) continue
    for (const fid of role.functionIds) {
      if (seen.has(fid)) continue
      seen.add(fid)
      const node = functionById.get(fid)
      if (node !== undefined) nodes.push(node)
    }
  }
  return { ...buildPermissionState(nodes), isSuperUser }
}
